import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  GameEngine,
  ReplayPlayer,
  createAudioSystem,
  type AudioSystem,
  type ReplayFile,
} from "@bastion/game-core";
import type { GameContent } from "@bastion/data";
import { GameRenderer, WebAudioProvider } from "@bastion/game-renderer";
import { subscribeGameAudioEvents } from "./replayAudio";
import {
  GameFacadeImpl,
  type GameFacade as GameFacadeType,
  type HudSnapshot,
  type BuildMode,
} from "./GameFacade";
import type { RuntimeSnapshot } from "@bastion/game-core";
import { Hud } from "./Hud";
import { BuildPanel } from "./BuildPanel";
import { SkillPanel } from "./SkillPanel";
import { EncyclopediaModal, DamageMatrixModal } from "./EncyclopediaModal";
import { ResultScreen } from "./ResultScreen";
import { SettingsPanel, type Difficulty } from "./SettingsPanel";
import { ManagementPanel } from "./ManagementPanel";
import { ReplayControls } from "./ReplayControls";
import { DifficultySelector, getStoredDifficulty } from "./DifficultySelector";
import { filterAvailableMaps, getAvailableMapId, getStoredMapId, saveMapId } from "./MapSelector";
import { LoadingScreen } from "./LoadingScreen";
import { I18nProvider } from "./i18n";

/** 在 localStorage 中持久化声音开关偏好的键。 */
const SOUND_ENABLED_KEY = "bastion-sound-enabled";

/**
 * Error boundary to catch React rendering errors and display them.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // 错误边界的备用文本——此处无 i18n 可用。
      return (
        <div
          className="bastion-app"
          data-testid="bastion-app"
          style={{ padding: "20px", color: "red" }}
        >
          <h1>应用加载失败</h1>
          <pre style={{ fontSize: "12px" }}>{this.state.error?.message}</pre>
          <pre style={{ fontSize: "12px" }}>{this.state.error?.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export interface BastionAppProps {
  content: GameContent;
  /** 页面模式；默认首页为 normal。 */
  mode?: "normal" | "debug";
}

/**
 * The single React mount point for one playable map.
 *
 *   - Owns the Phaser `GameRenderer` (mounted into a div, NOT driven by
 *     React state per-frame — CLAUDE.md §8, §20)
 *   - Runs the engine's fixed-timestep simulation on requestAnimationFrame,
 *     scaled by `gameSpeed`, and pushes each new state into the renderer
 *   - The React tree only re-renders on throttled HUD snapshots + discrete
 *     events, never per tick
 *   - On load: boot the engine/renderer, auto-start the selected map,
 *     and display the game HUD directly (no start overlay)
 */
export function BastionApp({ content, mode = "normal" }: BastionAppProps) {
  // 调试页跳过装饰性加载动画；E2E 模式也跳过以缩短玩法测试时间。
  const skipLoading =
    mode === "debug" || new URLSearchParams(window.location.search).get("e2e") === "1";
  const mountRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<GameRenderer | null>(null);
  const rafRef = useRef<number | null>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const facadeRef = useRef<GameFacadeType | null>(null);
  const audioSystemRef = useRef<AudioSystem | null>(null);
  const replayPlayerRef = useRef<ReplayPlayer | null>(null);
  const replayRendererRef = useRef<GameRenderer | null>(null);
  const replayAudioUnsubscribeRef = useRef<(() => void) | null>(null);
  const replayMountRef = useRef<HTMLDivElement | null>(null);
  const replayElapsedRef = useRef(0);
  /** 追踪当前在画布上方拖拽的塔类型。 */
  const dragOverModeRef = useRef<BuildMode | null>(null);

  const [selectedMapId, _setSelectedMapId] = useState<string>(() => getStoredMapId());
  const [engine, setEngine] = useState<GameEngine | null>(null);
  const [facade, setFacade] = useState<GameFacadeType | null>(null);
  const [snapshot, setSnapshot] = useState<HudSnapshot | null>(null);
  const [replayPlayer, setReplayPlayer] = useState<ReplayPlayer | null>(null);
  const [showEncyclopedia, setShowEncyclopedia] = useState(false);
  const [showDamageMatrix, setShowDamageMatrix] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showManagement, setShowManagement] = useState(false);
  const [showDifficulty, setShowDifficulty] = useState(false);
  const [difficulty, setDifficulty] = useState<Difficulty>(() => {
    return getStoredDifficulty();
  });
  /** 从 localStorage 读取的声音启用状态（启动时读取一次）。 */
  const _soundEnabled = (() => {
    try {
      const stored = localStorage.getItem(SOUND_ENABLED_KEY);
      if (stored !== null) return stored === "true";
    } catch {
      // 存储不可用——回退到默认值。
    }
    return true;
  })();
  const [isLoading, setIsLoading] = useState(!skipLoading);
  const [soundEnabled, setSoundEnabled] = useState(_soundEnabled);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState<1 | 2 | 4>(1);
  const [showReplayError, setShowReplayError] = useState("");
  const unbindReplayAudio = useCallback(() => {
    const unsubscribe = replayAudioUnsubscribeRef.current;
    replayAudioUnsubscribeRef.current = null;
    if (unsubscribe) unsubscribe();
  }, []);
  const handleReplayExit = useCallback(() => {
    replayElapsedRef.current = 0;
    unbindReplayAudio();
    replayRendererRef.current?.destroy();
    replayRendererRef.current = null;
    replayPlayerRef.current = null;
    setReplayPlayer(null);
    setReplayPlaying(false);
    setSnapshot(facadeRef.current?.snapshotNow() ?? null);
  }, []);
  const handleImportReplay = useCallback(
    (replay: ReplayFile) => {
      try {
        const player = new ReplayPlayer(content, replay);
        player.seek(0);
        replayPlayerRef.current = player;
        replayAudioUnsubscribeRef.current?.();
        replayAudioUnsubscribeRef.current = null;
        replayMountRef.current?.replaceChildren();
        replayRendererRef.current?.destroy();
        replayRendererRef.current = null;
        setReplayPlayer(player);
        setReplayPlaying(false);
        setShowSettings(false);
        setShowManagement(false);
        setShowEncyclopedia(false);
        setShowDamageMatrix(false);
        setShowReplayError("");
      } catch (error) {
        setShowReplayError(error instanceof Error ? error.message : String(error));
      }
    },
    [content],
  );

  const replaceReplayEngineRef = useRef<(replacement: GameEngine) => void>(() => undefined);
  const bindReplayAudio = useCallback(
    (engine: GameEngine) => {
      unbindReplayAudio();
      const audio = audioSystemRef.current;
      if (audio && soundEnabled) {
        replayAudioUnsubscribeRef.current = subscribeGameAudioEvents(engine, audio, content);
      }
    },
    [content, soundEnabled, unbindReplayAudio],
  );

  const handleReplaySeek = useCallback(
    (seconds: number): number => {
      const player = replayPlayerRef.current;
      const currentEngine = engineRef.current;
      if (!player || !currentEngine) return 0;
      replayElapsedRef.current = 0;
      replayAudioUnsubscribeRef.current?.();
      replayAudioUnsubscribeRef.current = null;
      try {
        const result = player.seek(seconds);
        if (result.engine !== currentEngine) replaceReplayEngineRef.current(result.engine);
        else {
          replayRendererRef.current?.sync(result.engine.state);
        }
        setSnapshot(facadeRef.current?.snapshotNow() ?? null);
        setShowReplayError("");
        return player.currentTime;
      } catch (error) {
        setReplayPlaying(false);
        setShowReplayError(error instanceof Error ? error.message : String(error));
        return player.currentTime;
      } finally {
        unbindReplayAudio();
        const activePlayer = replayPlayerRef.current;
        if (activePlayer) bindReplayAudio(activePlayer.getEngine());
      }
    },
    [bindReplayAudio, content, unbindReplayAudio],
  );

  const createReplayRenderer = useCallback(() => {
    const mountEl = replayMountRef.current;
    const replay = replayPlayerRef.current;
    if (!mountEl || !replay || replayRendererRef.current) return;
    const renderer = new GameRenderer({
      engine: replay.getEngine(),
      mapConfig: content.maps[replay.getEngine().getCurrentMapId() ?? selectedMapId],
      towers: content.towers,
      skills: content.skills,
      enemies: { ...content.enemies, ...content.bosses },
      showMapMarkers: mode !== "debug",
      target: mountEl,
      dispatch: (command) => replay.getEngine().dispatch(command),
    });
    replayRendererRef.current = renderer;
    bindReplayAudio(replay.getEngine());
    renderer.wakeActiveScene();
    renderer.sync(replay.state);
  }, [bindReplayAudio, content, mode, selectedMapId]);

  useEffect(() => {
    if (!replayPlayer) return;
    const frameId = requestAnimationFrame(createReplayRenderer);
    return () => cancelAnimationFrame(frameId);
  }, [createReplayRenderer, replayPlayer]);

  const replaceReplayEngine = useCallback(
    (replacement: GameEngine) => {
      const mountEl = replayMountRef.current;
      const replay = replayPlayerRef.current;
      if (!mountEl || !replay) throw new Error("回放弹窗画布尚未准备就绪");
      replayRendererRef.current?.destroy();
      replayRendererRef.current = null;
      replayAudioUnsubscribeRef.current?.();
      replayAudioUnsubscribeRef.current = null;
      mountEl.replaceChildren();
      const nextRenderer = new GameRenderer({
        engine: replacement,
        mapConfig: content.maps[replacement.getCurrentMapId() ?? selectedMapId],
        towers: content.towers,
        skills: content.skills,
        enemies: { ...content.enemies, ...content.bosses },
        showMapMarkers: mode !== "debug",
        target: mountEl,
        dispatch: (command) => replacement.dispatch(command),
      });
      nextRenderer.wakeActiveScene();
      nextRenderer.sync(replacement.state);
      replayRendererRef.current = nextRenderer;
      bindReplayAudio(replacement);
      replay.setEngine(replacement);
    },
    [bindReplayAudio, content, mode, selectedMapId],
  );

  useEffect(() => {
    replaceReplayEngineRef.current = replaceReplayEngine;
  }, [replaceReplayEngine]);

  /** 加载期间的占位快照——HUD 始终可渲染，面板交互通过 facade 为 null 禁用 */
  const placeholderSnapshot: HudSnapshot = {
    gold: 0,
    lives: 0,
    maxLives: 0,
    wave: 0,
    totalWaves: content.maps[selectedMapId]?.waves?.length ?? 0,
    isEndless: content.maps[selectedMapId]?.isEndless === true,
    waveInProgress: false,
    waveEnemiesRemaining: 0,
    waveEnemiesTotal: 0,
    waveCountdown: 0,
    status: "paused" as const,
    gameSpeed: 1,
    fps: 0,
    skills: [],
    isDebug: mode === "debug",
    unlimitedGold: mode === "debug",
    enemyTypes: [],
    towerTypes: Object.values(content.towers).map((t) => ({
      id: t.id,
      cost: t.cost,
      skinUrl: undefined,
    })),
    towers: [],
    selectedTowerId: null,
    selectedTowerPos: null,
    selectedEnemyId: null,
    selectedEnemyType: null,
    selectedTowerType: null,
    selectedTowerSkills: [],
    selectedTowerUpgradeCost: null,
  };

  /**
   * Boot the engine, renderer, and audio system once the mount node
   * and content are available. Re-boots when difficulty or selected
   * map changes so the engine picks up the new balance and map config.
   *
   * NOTE: We use a delayed check to ensure mountRef is populated after
   * the component first renders (StrictMode double-render can cause
   * mountRef.current to be null on first pass).
   */
  useEffect(() => {
    if (isLoading) {
      console.log("[BastionApp] Still loading, skipping bootstrap");
      return;
    }

    console.log("[BastionApp] Loading complete, bootstrapping...");
    const mountEl = mountRef.current;
    console.log("[BastionApp] mountEl from ref:", mountEl);

    if (!mountEl) {
      console.warn("[BastionApp] mountEl is null, trying DOM query...");
      const fallbackEl = document.querySelector(".canvas-mount") as HTMLElement | null;
      if (fallbackEl) {
        console.log("[BastionApp] Found fallback element via DOM query");
        return bootstrapGame(fallbackEl);
      }
      console.error("[BastionApp] Could not find mount element!");
      return;
    }

    return bootstrapGame(mountEl);
  }, [isLoading]);

  // 提取的初始化函数以避免代码重复
  function bootstrapGame(mountEl: HTMLElement) {
    console.log("[BastionApp] Bootstrapping game...");
    // 若浏览器可用则使用 Web Audio API 创建音频系统。
    // 在无头/测试环境中静默回退到 NoOpProvider。
    console.log("[BastionApp] Creating audio system...");
    const audioSystem = createAudioSystem({
      provider: new WebAudioProvider(),
      masterVolume: soundEnabled ? 1 : 0,
    });
    audioSystemRef.current = audioSystem;

    console.log("[BastionApp] Creating GameEngine...");
    let createdEngine = new GameEngine(content, {
      seed: 1337,
      difficulty: difficulty as "easy" | "normal" | "hard",
      mode,
    });
    createdEngine.setReplayRecording(true);
    console.log("[BastionApp] GameEngine created, state:", createdEngine.state.status);

    console.log("[BastionApp] Creating GameRenderer...");
    const createdRenderer = new GameRenderer({
      engine: createdEngine,
      mapConfig: content.maps[selectedMapId],
      towers: content.towers,
      skills: content.skills,
      enemies: { ...content.enemies, ...content.bosses },
      showMapMarkers: mode !== "debug",
      target: mountEl,
      dispatch: (command) => createdEngine.dispatch(command),
    });
    console.log("[BastionApp] GameRenderer created");
    rendererRef.current = createdRenderer;
    // 调试：暴露引擎以便 e2e 测试检查状态。
    (window as unknown as { __dbgEngine?: unknown }).__dbgEngine = createdEngine;

    console.log("[BastionApp] Creating GameFacade...");
    const createdFacade = new GameFacadeImpl(
      createdEngine,
      content,
      {
        setBuildMode: (buildMode: BuildMode | null) => createdRenderer.setBuildMode(buildMode),
        setPlacementRadius: (t: string | null) => createdRenderer.setPlacementRadius(t),
        setPlacementPosition: (position: { x: number; y: number } | null) =>
          createdRenderer.setPlacementPosition(position),
        selectTower: (t: string | null) => createdRenderer.selectTower(t),
        selectEnemy: (id: string | null) => createdRenderer.selectEnemy(id),
        onTowerSelected: (data) => createdRenderer.onTowerSelected?.(data),
        startSkillAim: (skillId: string) => createdRenderer.startSkillAim(skillId),
        cancelSkillAim: () => createdRenderer.cancelSkillAim(),
      },
      audioSystem,
    );
    console.log("[BastionApp] GameFacade created");

    // 调试：暴露 facade 以便 e2e 测试使用。
    (window as unknown as { __dbgFacade?: unknown }).__dbgFacade = createdFacade;

    console.log("[BastionApp] Setting state...");
    setEngine(createdEngine);
    engineRef.current = createdEngine;
    facadeRef.current = createdFacade;
    setFacade(createdFacade);

    // 唤醒渲染器使地图立即可见。
    createdRenderer.wakeActiveScene();
    setSnapshot(createdFacade.snapshotNow());
    console.log("[BastionApp] First snapshot:", createdFacade.snapshotNow());
    const unsubHud = createdFacade.onHudSnapshot(setSnapshot);
    console.log("[BastionApp] HUD subscription set up");

    // 自动在选中的地图上开始游戏。
    createdEngine.start(selectedMapId);
    createdFacade.setReplayMode(false);
    createdRenderer.sync(createdEngine.state);
    setSnapshot(createdFacade.snapshotNow());
    console.log("[BastionApp] After start snapshot:", createdFacade.snapshotNow());

    // 塔选择是 UI 的关注点，不是 game-core 命令。
    // 渲染器在非建造点击时回调地图坐标；
    // 我们通过 facade 对引擎的塔列表进行命中测试来驱动 BuildPanel 的选中状态
    // （与点击塔卡片的路径相同）。
    const selectTowerAt = (x: number, y: number) => {
      const eng = engineRef.current;
      if (!eng) return;
      // 按比例命中半径：最近塔身体半径的 1.5 倍。
      // 这与视觉大小匹配，避免选中相邻的塔。
      const mapCfg = content.maps[selectedMapId];
      const gridSize = mapCfg?.gridSize ?? 40;
      const towers = eng.state.towers;
      const hit = towers.reduce<NonNullable<(typeof towers)[number]> | null>((nearest, tower) => {
        const distance = Math.hypot(tower.position.x - x, tower.position.y - y);
        const config = content.towers[tower.type];
        const radiusCells = config?.bodyRadiusCells ?? 0.5;
        const bodyRadius = (radiusCells * gridSize) / 2;
        const hitRadius = bodyRadius * 1.5;
        if (distance >= hitRadius) return nearest;
        if (!nearest) return tower;
        const nearestDistance = Math.hypot(nearest.position.x - x, nearest.position.y - y);
        return distance < nearestDistance ? tower : nearest;
      }, null);
      createdFacade.selectTower(hit ? hit.id : null);
    };
    createdRenderer.onTowerClick = selectTowerAt;
    // 设置敌人点击处理程序
    createdRenderer.onEnemyClick = (enemyId: string) => {
      createdFacade.selectEnemy(
        enemyId === createdFacade.snapshotNow().selectedEnemyId ? null : enemyId,
      );
    };

    // 清理函数
    return () => {
      console.log("[BastionApp] Cleanup called");
      unsubHud();
      createdFacade.dispose();
      createdRenderer.destroy();
      replayAudioUnsubscribeRef.current?.();
      replayAudioUnsubscribeRef.current = null;
      replayRendererRef.current?.destroy();
      replayRendererRef.current = null;
      audioSystem.dispose();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if ((window as unknown as { __dbgEngine?: unknown }).__dbgEngine === createdEngine) {
        delete (window as unknown as { __dbgEngine?: unknown }).__dbgEngine;
      }
      if ((window as unknown as { __dbgFacade?: unknown }).__dbgFacade === createdFacade) {
        delete (window as unknown as { __dbgFacade?: unknown }).__dbgFacade;
      }
      rendererRef.current = null;
      engineRef.current = null;
      facadeRef.current = null;
      replayPlayerRef.current = null;
      replayRendererRef.current = null;
      audioSystemRef.current = null;
    };
  }

  // 当声音开关变化时将设置应用到当前音频系统。
  useEffect(() => {
    const system = audioSystemRef.current;
    if (!system) return;
    system.setMasterVolume(soundEnabled ? 1 : 0);
    const replay = replayPlayerRef.current;
    if (replay) bindReplayAudio(replay.getEngine());
  }, [bindReplayAudio, soundEnabled]);

  // 模拟循环：将引擎推进到"当前"时间并与渲染器同步。
  useEffect(() => {
    if (!engine) return;
    let lastReal = performance.now();
    let elapsedSinceHudUpdate = 0;

    const loop = (now: number) => {
      const e = engineRef.current;
      const replay = replayPlayerRef.current;
      const state = e?.state;
      const frameElapsed = Math.max(0, (now - lastReal) / 1000);
      if (replay && replayPlaying && !replay.isComplete) {
        replayElapsedRef.current += frameElapsed * replaySpeed;
        elapsedSinceHudUpdate += frameElapsed;
        const remaining = Math.max(0, replay.durationSeconds - replay.currentTime);
        const previousTime = replay.currentTime;
        const result = replay.advanceBy(Math.min(replayElapsedRef.current, remaining));
        replayElapsedRef.current = Math.max(
          0,
          replayElapsedRef.current - (replay.currentTime - previousTime),
        );
        if (elapsedSinceHudUpdate >= 0.1 || result.complete) {
          elapsedSinceHudUpdate = 0;
          const currentFacade = facadeRef.current;
          setSnapshot(currentFacade?.snapshotNow() ?? null);
        }
        replayRendererRef.current?.sync(result.engine.state);
        if (result.complete) {
          replayElapsedRef.current = 0;
          setReplayPlaying(false);
        }
      } else if (state && state.status === "running" && !replayPlayerRef.current) {
        const dt = Math.min(frameElapsed * state.gameSpeed, 0.25);
        e.advanceTo(e.time + dt);
      }
      lastReal = now;
      const renderer = rendererRef.current;
      if (e && renderer && !replayPlayerRef.current) renderer.sync(e.state);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [engine, replayPlaying, replaySpeed]);

  const won = snapshot?.status === "won";
  const lost = snapshot?.status === "lost";
  const ended = won || lost;

  const handleMapChange = useCallback(
    (mapId: string) => {
      const availableMapId = getAvailableMapId(mapId);
      if (availableMapId === selectedMapId) return;
      saveMapId(availableMapId);
      _setSelectedMapId(availableMapId);
      // 刷新页面以重新初始化引擎和渲染器
      location.reload();
    },
    [selectedMapId],
  );

  const handlePlayAgain = useCallback(() => {
    location.reload();
  }, []);

  const handleLoadingReady = useCallback(() => {
    setIsLoading(false);
  }, []);

  const mapPointFromClient = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const canvas = mountRef.current?.querySelector("canvas") as HTMLCanvasElement | null;
      const mapCfg = content.maps[selectedMapId];
      if (!canvas || !mapCfg) return null;
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      return {
        x: ((clientX - rect.left) / rect.width) * mapCfg.width,
        y: ((clientY - rect.top) / rect.height) * mapCfg.height,
      };
    },
    [content, selectedMapId],
  );

  const clearDragPlacement = useCallback(() => {
    dragOverModeRef.current = null;
    facade?.setPlacementPosition(null);
    facade?.setBuildMode(null);
  }, [facade]);

  /** 处理画布放置：将页面坐标转换为地图坐标并发出建造命令。 */
  const handleCanvasDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const buildMode = dragOverModeRef.current;
      const position = mapPointFromClient(e.clientX, e.clientY);
      if (buildMode && position && facade) {
        try {
          if (buildMode.kind === "tower") facade.buildTower(buildMode.type, position);
          else facade.buildEnemyDebug(buildMode.type, position);
        } catch (err) {
          console.warn("[BastionApp] build_tower rejected on drop:", err);
        } finally {
          clearDragPlacement();
        }
        return;
      }
      clearDragPlacement();
    },
    [clearDragPlacement, facade, mapPointFromClient],
  );

  const handleCanvasDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (!dragOverModeRef.current || !facade) return;
      const position = mapPointFromClient(e.clientX, e.clientY);
      if (position) facade.setPlacementPosition(position);
    },
    [facade, mapPointFromClient],
  );

  const handleDragStartBuild = useCallback(
    (buildMode: BuildMode) => {
      dragOverModeRef.current = buildMode;
      facade?.setBuildMode(buildMode);
    },
    [facade],
  );

  const handleDragEnd = useCallback(() => {
    clearDragPlacement();
  }, [clearDragPlacement]);

  return (
    <I18nProvider>
      <div className="bastion-app" data-testid="bastion-app">
        {/* HUD 始终可见——加载期间显示占位快照 */}
        <Hud
          snapshot={snapshot ?? placeholderSnapshot}
          facade={facade!}
          maps={filterAvailableMaps(content.maps)}
          currentMapId={getAvailableMapId(selectedMapId)}
          onMapChange={facade ? handleMapChange : undefined}
          onSettingsOpen={facade ? () => setShowSettings(true) : undefined}
          onManagementOpen={facade ? () => setShowManagement(true) : undefined}
          replayMode={facade?.isReplayMode() ?? false}
        />

        <div className="bastion-main">
          {/* 左侧建造面板——加载期间不可交互 */}
          {facade && (
            <BuildPanel
              snapshot={snapshot ?? placeholderSnapshot}
              facade={facade}
              onDragStartBuild={handleDragStartBuild}
              onDragEnd={handleDragEnd}
            />
          )}

          {/* 画布挂载点：加载时叠加遮罩 */}
          <div
            className="canvas-mount"
            ref={mountRef}
            data-testid="game-canvas"
            onDrop={handleCanvasDrop}
            onDragOver={handleCanvasDragOver}
          >
            {isLoading && <LoadingScreen onReady={handleLoadingReady} />}
          </div>

          {/* 右侧技能面板——加载期间不可交互 */}
          {facade && (
            <SkillPanel
              facade={facade}
              snapshot={snapshot ?? placeholderSnapshot}
              towerConfigs={content.towers}
              enemyConfigs={{ ...content.enemies, ...content.bosses }}
            />
          )}

          {ended && snapshot && facade && (
            <ResultScreen facade={facade} onClose={handlePlayAgain} />
          )}
        </div>

        {showEncyclopedia && (
          <EncyclopediaModal content={content} onClose={() => setShowEncyclopedia(false)} />
        )}
        {showDamageMatrix && (
          <DamageMatrixModal content={content} onClose={() => setShowDamageMatrix(false)} />
        )}

        {replayPlayer && (
          <ReplayControls
            player={replayPlayer}
            content={content}
            playing={replayPlaying}
            playbackSpeed={replaySpeed}
            canvasHostRef={replayMountRef}
            onPlayingChange={setReplayPlaying}
            onAudioUnlock={async () => {
              const audio = audioSystemRef.current;
              if (!audio?.unlock) return false;
              return audio.unlock();
            }}
            onPlaybackSpeedChange={setReplaySpeed}
            onSeek={handleReplaySeek}
            onExit={handleReplayExit}
          />
        )}
        {showReplayError && (
          <div className="replay-error" role="alert" data-testid="replay-error">
            {showReplayError}
          </div>
        )}

        {showSettings && facade && (
          <SettingsPanel
            difficulty={difficulty}
            onDifficultyChange={(d) => setDifficulty(d)}
            onClose={() => setShowSettings(false)}
            soundEnabled={soundEnabled}
            onSoundToggle={() => {
              setSoundEnabled((previous) => {
                const next = !previous;
                localStorage.setItem(SOUND_ENABLED_KEY, String(next));
                audioSystemRef.current?.setMasterVolume(next ? 1 : 0);
                return next;
              });
            }}
            onEncyclopediaOpen={() => {
              setShowSettings(false);
              setShowEncyclopedia(true);
            }}
            onDamageMatrixOpen={() => {
              setShowSettings(false);
              setShowDamageMatrix(true);
            }}
          />
        )}

        {showManagement && facade && (
          <ManagementPanel
            onClose={() => setShowManagement(false)}
            canImportProgress={facade.canImportRuntimeSnapshot()}
            onExportProgress={() => facade.exportRuntimeSnapshot()}
            onImportProgress={(runtimeSnapshot: RuntimeSnapshot) => {
              facade.importRuntimeSnapshot(runtimeSnapshot);
              const currentEngine = engineRef.current;
              if (currentEngine) rendererRef.current?.sync(currentEngine.state);
              setSnapshot(facade.snapshotNow());
            }}
            onExportReplay={() => facade.exportReplayFile()}
            onImportReplay={handleImportReplay}
            replayMode={facade.isReplayMode()}
          />
        )}

        {showDifficulty && (
          <DifficultySelector
            onSelect={(key) => {
              setDifficulty(key);
              setShowDifficulty(false);
            }}
            onClose={() => setShowDifficulty(false)}
          />
        )}
      </div>
    </I18nProvider>
  );
}
